import React, { useEffect, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Send, ShieldAlert, Loader2, Zap } from "lucide-react";

const ALLOWED_ROLES = ["owner", "manager", "administrator"];

export default function YugBrain() {
    const { user } = useAuth();
    const allowed = user && ALLOWED_ROLES.includes(user.role);

    const [question, setQuestion] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [answer, setAnswer] = useState(null); // { narrative, rows }
    const [meta, setMeta] = useState(null); // { asked_at, asked_by, model, question }
    const [loading, setLoading] = useState(false);
    const textareaRef = useRef(null);

    useEffect(() => {
        if (!allowed) return;
        api.get("/yug-brain/suggestions")
            .then(({ data }) => setSuggestions(data.suggestions || []))
            .catch(() => {});
    }, [allowed]);

    const ask = async (q) => {
        const text = (q ?? question).trim();
        if (!text || loading) return;
        setLoading(true);
        setAnswer(null);
        setMeta(null);
        try {
            const { data } = await api.post("/yug-brain/query", { question: text });
            setAnswer(data.answer);
            setMeta({ ...data, question: text });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            ask();
        }
    };

    if (!allowed) {
        return (
            <div data-testid="page-yug-brain">
                <PageHeader
                    title="Yug Brain"
                    description="Restricted to owner, manager, and administrator roles."
                />
                <div className="mx-8 my-8 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    <ShieldAlert className="h-4 w-4" strokeWidth={1.5} />
                    You do not have permission to use Yug Brain.
                </div>
            </div>
        );
    }

    return (
        <div data-testid="page-yug-brain">
            <PageHeader
                title="Yug Brain"
                description="Ask anything about your business — inventory, sales, receivables, activity. Answers use live YugOS data."
                actions={
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[10px] font-medium uppercase tracking-widest text-violet-700">
                        <Sparkles className="h-3 w-3" strokeWidth={1.75} /> Claude Sonnet 4.5
                    </span>
                }
            />

            <div className="mx-auto max-w-3xl px-8 py-8">
                {/* Composer */}
                <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
                    <Textarea
                        ref={textareaRef}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Ask Yug Brain… e.g. Which invoices are overdue?"
                        rows={3}
                        maxLength={1000}
                        disabled={loading}
                        data-testid="yug-brain-input"
                        className="min-h-[92px] resize-none border-0 bg-transparent px-4 py-3 text-[15px] leading-relaxed focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <div className="flex items-center justify-between border-t border-zinc-100 px-3 py-2">
                        <div className="text-[11px] font-mono text-zinc-400">
                            {question.length}/1000
                            <span className="mx-2">·</span>
                            <kbd className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5">⌘</kbd>
                            <span className="mx-1">+</span>
                            <kbd className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5">↵</kbd>{" "}
                            to ask
                        </div>
                        <Button
                            onClick={() => ask()}
                            disabled={!question.trim() || loading}
                            size="sm"
                            className="h-8 gap-1.5"
                            data-testid="yug-brain-ask-button"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                                </>
                            ) : (
                                <>
                                    <Send className="h-3.5 w-3.5" /> Ask
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Suggestions (empty state) */}
                {!answer && !loading && suggestions.length > 0 && (
                    <div className="mt-6" data-testid="yug-brain-suggestions">
                        <div className="mb-2 text-[10px] font-medium uppercase tracking-widest text-zinc-400">
                            Try asking
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {suggestions.map((s, i) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => {
                                        setQuestion(s);
                                        ask(s);
                                    }}
                                    data-testid={`yug-brain-suggestion-${i}`}
                                    className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-700 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Loading skeleton */}
                {loading && (
                    <div className="mt-8 rounded-xl border border-zinc-200 bg-white p-6" data-testid="yug-brain-loading">
                        <div className="flex items-center gap-2 text-sm text-zinc-500">
                            <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
                            Consulting Yug Brain over live business data…
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="h-3 w-11/12 animate-pulse rounded bg-zinc-100" />
                            <div className="h-3 w-9/12 animate-pulse rounded bg-zinc-100" />
                            <div className="h-3 w-8/12 animate-pulse rounded bg-zinc-100" />
                        </div>
                    </div>
                )}

                {/* Answer */}
                {answer && !loading && (
                    <div className="mt-8 space-y-4" data-testid="yug-brain-answer">
                        {meta?.question && (
                            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-700">
                                <div className="mb-1 text-[10px] font-medium uppercase tracking-widest text-zinc-400">
                                    You asked
                                </div>
                                {meta.question}
                            </div>
                        )}

                        <div className="rounded-xl border border-violet-100 bg-gradient-to-b from-white to-violet-50/30 p-5 shadow-sm">
                            <div className="mb-3 flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-violet-700">
                                <Zap className="h-3 w-3" strokeWidth={2} />
                                Yug Brain
                            </div>
                            <p
                                className="whitespace-pre-wrap text-[15px] leading-relaxed text-zinc-800"
                                data-testid="yug-brain-narrative"
                            >
                                {answer.narrative}
                            </p>

                            {Array.isArray(answer.rows) && answer.rows.length > 0 && (
                                <div className="mt-5 overflow-hidden rounded-md border border-zinc-200 bg-white" data-testid="yug-brain-rows">
                                    <table className="w-full text-sm">
                                        <tbody>
                                            {answer.rows.map((r, i) => (
                                                <tr
                                                    key={`${r.label ?? "row"}-${i}`}
                                                    className={i % 2 ? "bg-zinc-50/50" : ""}
                                                    data-testid={`yug-brain-row-${i}`}
                                                >
                                                    <td className="px-4 py-2 text-zinc-500">
                                                        {r.label ?? "—"}
                                                    </td>
                                                    <td className="px-4 py-2 text-right font-mono text-zinc-900">
                                                        {r.value ?? "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {meta && (
                            <div className="flex items-center justify-between text-[11px] font-mono text-zinc-400">
                                <span>{new Date(meta.asked_at).toLocaleString()}</span>
                                <span>{meta.model}</span>
                            </div>
                        )}

                        <div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setAnswer(null);
                                    setMeta(null);
                                    setQuestion("");
                                    textareaRef.current?.focus();
                                }}
                                data-testid="yug-brain-new-question"
                            >
                                Ask another question
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
